import { SoAPool } from './SoAPool';

/** Familles visuelles de projectiles. La couleur est une PROPRIÉTÉ DE
 *  SIMULATION et non de rendu, parce qu'elle porte de l'information de
 *  gameplay : les palettes sont strictement réservées (PLAN.md §4.5) et un
 *  projectile ennemi n'emprunte jamais la teinte d'un tir joueur. */
export const Fam = {
  EnemyRed: 0,
  EnemyPink: 1,
  EnemyPurple: 2,
  Player: 3,
  /** Tir SECONDAIRE du joueur.
   *
   *  ⚠️ Une famille distincte du tir primaire, et pas une commodité de rendu.
   *  Retour de jeu : « les armes secondaires n'ont aucune identité visuelle,
   *  ça se confond avec l'arme principale ». Les deux partageaient `Player`,
   *  donc la même teinte et le même lot de rendu — un joueur ne pouvait pas
   *  voir que son clic droit avait produit quelque chose.
   *
   *  L'information à porter est « ceci est l'arme que j'ai choisie au
   *  briefing » : elle mérite sa propre couleur, au même titre que les trois
   *  familles ennemies ont les leurs. */
  PlayerSecondary: 4,
  /** Tir des DRONES d'escorte.
   *
   *  ⚠️ Distinct du tir de fuselage, sur retour de jeu : « les drones tirent
   *  des balles aussi grosses que les miennes, de même calibre ». Ils
   *  partageaient `Player`, donc la même taille et la même teinte. Un drone est
   *  un appareil d'appui, pas une copie du vaisseau — son tir doit se lire comme
   *  un calibre plus léger : plus petit, plus rapide, moins de mordant unitaire.
   *
   *  ⚠️ Le DPS total est CONSERVÉ. Ce que les drones perdent en dégâts unitaires,
   *  le fuselage le récupère (voir `playerSystem`) : la courbe de puissance et
   *  les PV des boss, calibrés sur `cadence × nombre × dégâts`, restent valides.
   *  L'identité visuelle ne coûte rien à l'équilibrage — c'était la condition. */
  PlayerDrone: 5,
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   COMPORTEMENTS de projectile.

   Jusqu'ici un projectile n'était qu'un point qui avance et meurt sur ce qu'il
   touche. Les six premières secondaires ne demandaient rien de plus : elles se
   distinguent par la GÉOMÉTRIE de leur volée, jamais par ce que fait un
   projectile une fois parti. C'est la limite qu'a atteinte le catalogue — six
   façons d'envoyer des points droit devant se ressemblent forcément, quelle que
   soit la finesse de l'éventail.

   Un mode ajoute une CONDUITE : infléchir sa course, survivre au contact,
   rester accroché au vaisseau. C'est une extension de capacité du moteur, pas
   du contenu, et elle se paie en état — d'où les tableaux ci-dessous.

   ── Pourquoi un entier et pas des drapeaux combinables ────────────────────
   Un projectile a une conduite, une seule, et les combinaisons n'ont pas de
   sens : un rayon ancré ne peut pas « aussi » être guidé, il ne se déplace
   pas. Un masque de bits laisserait écrire ces états impossibles et il
   faudrait les arbitrer quelque part. Un entier les rend inexprimables.

   ── L'ordre des valeurs porte une information ─────────────────────────────
   ⚠️ Tout ce qui est ≥ `PB_PERSISTENT` SURVIT au contact et est donc ignoré par
   la première passe de `collisionSystem`, qui libère sur la première touche.
   Ces projectiles sont traités par `secondaryHitPass`. Réordonner ces valeurs
   fait entrer un disque dans la boucle qui le tue au premier ennemi. */
export const PbMode = {
  /** Balistique : ce que fait un projectile depuis le premier jour. */
  Ballistic: 0,
  /** Infléchit sa course vers une cible acquise devant lui. Meurt au contact
   *  comme n'importe quel projectile — la passe 1 s'en occupe. */
  Homing: 1,
  /** Traverse `hits` ennemis, un seul contact par ennemi consécutif. */
  Pierce: 2,
  /** Saute d'un ennemi au suivant : acquisition à courte portée, virage
   *  instantané, `hits` contacts au total. */
  Arc: 3,
  /** Ancré au vaisseau : ne se déplace pas de lui-même, persiste, et réémet
   *  ses dégâts à cadence fixe tant qu'il vit. */
  Beam: 4,
} as const;

/** Premier mode qui survit au contact. Voir l'avertissement ci-dessus. */
export const PB_PERSISTENT = PbMode.Pierce;

/**
 * Projectiles en Structure-of-Arrays.
 *
 * Un tableau typé par attribut, pas un tableau d'objets : quand la boucle de
 * collision parcourt 2 000 balles, elle ne lit que x et y. En SoA ce sont deux
 * parcours linéaires ; en tableau d'objets, chaque lecture traîne toute
 * l'entité dans la ligne de cache et on paie 6× la bande passante utile.
 *
 * `px`/`py` servent à deux choses distinctes, et c'est ce qui justifie de les
 * stocker plutôt que de les recalculer :
 *   - l'interpolation au rendu (position d'il y a un tick, mêlée à alpha)
 *   - la collision BALAYÉE (le segment réellement parcouru pendant le tick)
 */
export class BulletPool {
  readonly slots: SoAPool;

  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly px: Float32Array;
  readonly py: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  /** Rayon VISUEL. La hitbox effective en vaut 85 % (PLAN.md §4.4) : la marge
   *  est offerte au joueur, et la lecture visuelle devient littéralement
   *  exacte puisque le noyau blanc du sprite est la zone dangereuse. */
  readonly r: Float32Array;
  readonly damage: Float32Array;
  readonly fam: Uint8Array;
  /** 1 dès que la balle a été comptabilisée en graze — un frôlement ne
   *  rapporte qu'une fois, sinon rester collé à une balle ferait défiler le
   *  score indéfiniment. */
  readonly grazed: Uint8Array;

  /* ── État de comportement ─────────────────────────────────────────────
     Cinq tableaux, et pas un objet par projectile : la règle du pool est que
     l'on ajoute une COLONNE, jamais une ligne. Un objet par balle rendrait
     l'allocation proportionnelle au nombre de tirs — c'est-à-dire un passage du
     ramasse-miettes au pire moment, celui où l'écran est plein.

     Ils sont tous remis à zéro par `spawn`, ce qui n'est pas une précaution de
     style : un slot recyclé qui garderait le budget de perforation de son
     prédécesseur donnerait un tir primaire traversant, une fois sur mille,
     après vingt minutes de jeu. */

  /** Conduite du projectile — une valeur de `PbMode`. 0 = balistique. */
  readonly mode: Uint8Array;
  /** Paramètre de la conduite. Son sens dépend de `mode`, exactement comme
   *  `spacing` dépend de la forme d'une secondaire :
   *    · Homing / Pierce / Arc → vitesse de rotation, en radians par seconde ;
   *    · Beam                  → hauteur d'ancrage au-dessus de la bouche. */
  readonly param: Float32Array;
  /** Secondes restantes avant le changement d'état du projectile : bascule en
   *  retour pour un disque, expiration pour un segment de rayon. */
  readonly life: Float32Array;
  /** Contacts qu'il reste à produire. 0 sur un projectile persistant = il meurt
   *  au prochain. Non lu par la passe 1, qui libère de toute façon. */
  readonly hits: Uint8Array;
  /** Secondes avant que ce projectile puisse blesser de nouveau (rayon). */
  readonly hitCd: Float32Array;
  /** `uid` du dernier ennemi frappé.
   *
   *  ⚠️ Un uid, pas un slot : les slots sont recyclés, et un disque qui
   *  mémoriserait « slot 12 » refuserait de couper l'ennemi entré à sa place
   *  une seconde plus tard. C'est le même piège que `parentUid` dans
   *  `EnemyPool`, et il se manifeste de la même façon — jamais en test court,
   *  systématiquement en partie longue. */
  readonly lastUid: Int32Array;
  /** Avertissement restant, en secondes, avant qu'un segment de faisceau PLEINE
   *  HAUTEUR ne descende dans la zone du joueur (voir `EbMode.Beam` dans
   *  `enemyBulletSystem`).
   *
   *  Tant qu'il est > 0, le segment est PARQUÉ à l'ancre (en haut, hors de
   *  portée) : il existe donc — donc il est comptabilisé et rendu — mais ne peut
   *  frapper personne. C'est ce qui télégraphe un faisceau qui va traverser la
   *  ligne des −180 sans lequel il serait une mort non attribuable. Toujours nul
   *  sur un tir joueur et sur une balle ordinaire. */
  readonly warn: Float32Array;

  constructor(capacity: number) {
    this.slots = new SoAPool(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.px = new Float32Array(capacity);
    this.py = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.r = new Float32Array(capacity);
    this.damage = new Float32Array(capacity);
    this.fam = new Uint8Array(capacity);
    this.grazed = new Uint8Array(capacity);
    this.mode = new Uint8Array(capacity);
    this.param = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.hits = new Uint8Array(capacity);
    this.hitCd = new Float32Array(capacity);
    this.lastUid = new Int32Array(capacity);
    this.warn = new Float32Array(capacity);
  }

  get count(): number {
    return this.slots.count;
  }
  at(i: number): number {
    return this.slots.at(i);
  }

  /** Retourne le slot alloué, ou -1 si saturé. Le refus est silencieux côté
   *  pool : c'est à l'appelant de comptabiliser (World.overflow). */
  spawn(
    x: number,
    y: number,
    vx: number,
    vy: number,
    r: number,
    damage: number,
    fam: number,
  ): number {
    const s = this.slots.alloc();
    if (s < 0) return -1;
    this.x[s] = x;
    this.y[s] = y;
    // px/py = x/y à la naissance : le segment balayé du premier tick est
    // dégénéré, ce qui est correct — la balle n'a encore parcouru aucune
    // distance. L'oublier ferait apparaître un segment fantôme depuis la
    // position du précédent occupant du slot, et donc des touches inexpliquées.
    this.px[s] = x;
    this.py[s] = y;
    this.vx[s] = vx;
    this.vy[s] = vy;
    this.r[s] = r;
    this.damage[s] = damage;
    this.fam[s] = fam;
    this.grazed[s] = 0;
    // ⚠️ Un slot recyclé hérite sinon de la conduite de son prédécesseur. Le
    // tir primaire deviendrait perforant par accident, proportionnellement au
    // recyclage — donc jamais dans un test court, toujours en partie longue.
    this.mode[s] = 0;
    this.param[s] = 0;
    this.life[s] = 0;
    this.hits[s] = 0;
    this.hitCd[s] = 0;
    this.lastUid[s] = 0;
    // Un segment de faisceau recyclé qui garderait l'avertissement de son
    // prédécesseur naîtrait parqué en haut alors qu'il devrait être létal — un
    // trou dans la lame, invisible en test court.
    this.warn[s] = 0;
    return s;
  }

  /** Donne une conduite au projectile qui vient de naître.
   *
   *  Séparée de `spawn` plutôt qu'ajoutée en sept paramètres de plus : le tir
   *  primaire et les projectiles ennemis représentent l'écrasante majorité des
   *  appels et n'ont aucune conduite à décrire. Une signature à quatorze
   *  arguments dont sept valent zéro finit toujours par être appelée dans le
   *  mauvais ordre. */
  behave(slot: number, mode: number, param: number, life: number, hits: number): void {
    this.mode[slot] = mode;
    this.param[slot] = param;
    this.life[slot] = life;
    this.hits[slot] = hits;
  }

  free(slot: number): void {
    this.slots.free(slot);
  }

  clear(): void {
    this.slots.clear();
  }

  /** Recopie x→px pour tout le monde, avant l'intégration du tick. */
  savePrev(): void {
    const n = this.slots.count;
    for (let i = 0; i < n; i++) {
      const s = this.slots.at(i);
      this.px[s] = this.x[s]!;
      this.py[s] = this.y[s]!;
    }
  }
}
